import { Temporal } from "@js-temporal/polyfill";
import { advance, normalizeMemberSchedule, scheduleExceptionKey } from "./merge.js";
/**
 * La cache e' agganciata all'identita' dell'oggetto `Gym`: i documenti sono
 * immutabili per costruzione — ogni modifica ne produce uno nuovo — quindi un
 * gym cambiato non puo' rileggere voci vecchie, e un gym abbandonato porta via
 * la propria cache con se'. Prima di questa cache `gym.exceptions.find`, il
 * filtro degli slot e l'aritmetica `Temporal` degli istanti — il costo dominante
 * misurato in PERF-05, dove vive anche PERF-09 — si ripetevano per ogni atleta.
 */
const dayContextsByGym = new WeakMap();
function dayContextFor(gym, isoDate) {
    let contexts = dayContextsByGym.get(gym);
    if (!contexts) {
        contexts = new Map();
        dayContextsByGym.set(gym, contexts);
    }
    const cached = contexts.get(isoDate);
    if (cached)
        return cached;
    const date = Temporal.PlainDate.from(isoDate);
    const calendarException = gym.exceptions.find((entry) => entry.date === isoDate);
    const closed = Boolean(calendarException?.closed);
    const removed = new Set(calendarException?.removedSlotIds ?? []);
    const slots = closed
        ? []
        : [
            ...gym.slots.filter((slot) => slot.active && slot.weekday === date.dayOfWeek && !removed.has(slot.id)),
            ...(calendarException?.replacementSlots ?? []),
        ].sort((a, b) => a.startTime.localeCompare(b.startTime));
    const context = {
        date: isoDate,
        closed,
        slots,
        slotsById: new Map(slots.map((slot) => [slot.id, slot])),
        occurrenceBySlotId: new Map(slots.map((slot) => [slot.id, toOccurrence(gym, date, slot)])),
    };
    contexts.set(isoDate, context);
    return context;
}
/** La lettura per il calendario: risolta una volta per data, non per consumatore. */
export function gymDayContext(gym, date) {
    return dayContextFor(gym, date);
}
export function availableSlotsForDate(gym, date) {
    return dayContextFor(gym, date).slots;
}
function toOccurrence(gym, date, slot) {
    const time = Temporal.PlainTime.from(slot.startTime);
    const start = date.toZonedDateTime({ timeZone: gym.timeZone, plainTime: time });
    return {
        key: `${date.toString()}:${slot.id}`,
        date: date.toString(),
        sourceSlotId: slot.id,
        slotId: slot.id,
        startsAt: start.toInstant().toString(),
        endsAt: start.add({ minutes: gym.slotDurationMinutes }).toInstant().toString(),
        status: "scheduled",
    };
}
/**
 * L'occorrenza dal contesto della giornata: l'aritmetica degli istanti e' gia'
 * fatta, qui si cambia al piu' la provenienza (uno spostamento conserva lo slot
 * d'origine). I template sono condivisi e mai mutati: chi deve cambiarne lo
 * stato ne fa una copia, come gia' fa `withStatus`.
 */
function occurrenceFrom(context, slot, sourceSlotId = slot.id) {
    const template = context.occurrenceBySlotId.get(slot.id);
    if (!template)
        throw new Error(`Slot ${slot.id} fuori dal contesto della data ${context.date}`);
    return sourceSlotId === template.sourceSlotId ? template : { ...template, sourceSlotId };
}
export function slotOccurrencesForDate(gym, date) {
    const context = dayContextFor(gym, date);
    return context.slots.map((slot) => occurrenceFrom(context, slot));
}
/**
 * Il giorno dopo, senza passare dal polyfill: le date del calendario sono ISO e
 * l'aritmetica a mezzogiorno UTC non attraversa mai un cambio d'ora. Sta nel
 * ciclo piu' interno della proiezione, dove `Temporal.PlainDate.add` costava
 * piu' di tutto il resto (PERF-09).
 */
export function addDaysToIsoDate(value, days) {
    const date = new Date(`${value}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
function findException(schedule, date, sourceSlotId) {
    return schedule.exceptions.find((entry) => entry.kind !== "added" && entry.date === date && entry.sourceSlotId === sourceSlotId);
}
function exceptionSlotId(exception) {
    return exception.kind === "added" ? exception.slotId : exception.sourceSlotId;
}
/**
 * Una sola decisione per occorrenza, sempre sostituita e mai rimossa: la
 * sostituzione si propaga, l'assenza no. `cleared` e' la decisione "nessuna
 * eccezione", cioe' il ritorno alla ricorrenza.
 */
function putExceptions(schedule, replacements) {
    const keys = new Set(replacements.map(scheduleExceptionKey));
    return [
        ...schedule.exceptions.filter((entry) => !keys.has(scheduleExceptionKey(entry))),
        ...replacements,
    ];
}
/**
 * La revisione della decisione nuova batte sempre quella che sostituisce, anche
 * se cade nello stesso millisecondo: due tocchi rapidi sulla stessa occorrenza
 * sono la normalita' su un telefono.
 */
function exceptionRevisionAt(schedule, key, now) {
    const existing = schedule.exceptions.find((entry) => scheduleExceptionKey(entry) === key);
    return advance(now, existing?.updatedAt);
}
function clearedAt(schedule, date, sourceSlotId, now) {
    return {
        kind: "cleared",
        date,
        sourceSlotId,
        updatedAt: exceptionRevisionAt(schedule, `${date}:${sourceSlotId}`, now),
        updatedBy: schedule.memberId,
    };
}
function putRecurring(schedule, changes, now) {
    const stamped = changes.map((change) => ({
        slotId: change.slotId,
        active: change.active,
        updatedAt: advance(now, schedule.recurring.find((entry) => entry.slotId === change.slotId)?.updatedAt),
        updatedBy: schedule.memberId,
    }));
    const touched = new Set(stamped.map((entry) => entry.slotId));
    return [...schedule.recurring.filter((entry) => !touched.has(entry.slotId)), ...stamped];
}
/**
 * Gli impegni di una singola giornata, senza riguardo per l'ora di adesso: e' la
 * lettura su cui poggia il vincolo "uno slot al giorno", e quel vincolo non
 * cambia perche' la mattina e' passata.
 */
function occurrencesOnDate(gym, schedule, isoDate) {
    const context = dayContextFor(gym, isoDate);
    const availableById = context.slotsById;
    const withStatus = (occurrence, conflictKey) => ({
        ...occurrence,
        status: schedule.conflicts.some((entry) => entry.occurrenceKey === conflictKey && !entry.resolvedTo)
            ? "conflicted"
            : "scheduled",
    });
    const occurrences = [];
    for (const sourceSlotId of schedule.recurringSlotIds) {
        const source = availableById.get(sourceSlotId);
        if (!source)
            continue;
        const exception = findException(schedule, isoDate, sourceSlotId);
        if (exception?.kind === "cancelled")
            continue;
        const target = exception?.kind === "moved" ? availableById.get(exception.targetSlotId) : source;
        if (!target)
            continue;
        occurrences.push(withStatus(occurrenceFrom(context, target, sourceSlotId), `${isoDate}:${sourceSlotId}`));
    }
    for (const exception of schedule.exceptions) {
        if (exception.kind !== "added" || exception.date !== isoDate)
            continue;
        const slot = availableById.get(exception.slotId);
        if (!slot)
            continue;
        const occurrence = occurrenceFrom(context, slot);
        occurrences.push(withStatus(occurrence, occurrence.key));
    }
    return occurrences.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}
export function occurrencesForDate(gym, schedule, date) {
    return occurrencesOnDate(gym, schedule, Temporal.PlainDate.from(date).toString());
}
export function occurrencesBetween(gym, schedule, fromInstant, days) {
    const from = Temporal.Instant.from(fromInstant);
    const startDate = from.toZonedDateTimeISO(gym.timeZone).toPlainDate().toString();
    // Il confronto avviene sui millisecondi: gli istanti delle occorrenze escono
    // da `toInstant().toString()`, quindi `Date.parse` legge sempre lo stesso
    // formato. `Temporal.Instant.from` per occorrenza era il punto piu' caldo del
    // profilo dopo la risoluzione degli slot (PERF-09).
    const fromMs = from.epochMilliseconds;
    const occurrences = [];
    for (let offset = 0; offset <= days; offset += 1) {
        const date = offset === 0 ? startDate : addDaysToIsoDate(startDate, offset);
        for (const occurrence of occurrencesOnDate(gym, schedule, date)) {
            if (Date.parse(occurrence.startsAt) >= fromMs) {
                occurrences.push(occurrence);
            }
        }
    }
    return occurrences.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}
/**
 * Ogni modifica parte dalla forma normale: senza, una pianificazione arrivata
 * dalle prime versioni — con gli orari elencati ma senza le loro revisioni —
 * perderebbe tutte le ricorrenze alla prima scrittura.
 */
export function bookOccurrence(schedule, date, slotId, now = new Date().toISOString()) {
    const base = normalizeMemberSchedule(schedule);
    const replacement = base.recurringSlotIds.includes(slotId)
        ? clearedAt(base, date, slotId, now)
        : {
            kind: "added",
            date,
            slotId,
            updatedAt: exceptionRevisionAt(base, `${date}:${slotId}`, now),
            updatedBy: base.memberId,
        };
    return normalizeMemberSchedule({
        ...base,
        exceptions: putExceptions(base, [replacement]),
        updatedAt: advance(now, base.updatedAt),
    });
}
export function moveOccurrence(schedule, date, sourceSlotId, targetSlotId, now = new Date().toISOString()) {
    const base = normalizeMemberSchedule(schedule);
    const movesAddedOccurrence = base.exceptions.some((entry) => entry.kind === "added" && entry.date === date && entry.slotId === sourceSlotId);
    // Spostare una presenza aggiunta cambia la chiave dell'occorrenza: quella
    // vecchia va sepolta, non dimenticata.
    const replacements = movesAddedOccurrence
        ? [
            clearedAt(base, date, sourceSlotId, now),
            {
                kind: "added",
                date,
                slotId: targetSlotId,
                updatedAt: exceptionRevisionAt(base, `${date}:${targetSlotId}`, now),
                updatedBy: base.memberId,
            },
        ]
        : [
            {
                kind: "moved",
                date,
                sourceSlotId,
                targetSlotId,
                updatedAt: exceptionRevisionAt(base, `${date}:${sourceSlotId}`, now),
                updatedBy: base.memberId,
            },
        ];
    return normalizeMemberSchedule({
        ...base,
        exceptions: putExceptions(base, replacements),
        updatedAt: advance(now, base.updatedAt),
    });
}
export function cancelOccurrence(schedule, date, sourceSlotId, now = new Date().toISOString()) {
    const base = normalizeMemberSchedule(schedule);
    const cancelsAddedOccurrence = base.exceptions.some((entry) => entry.kind === "added" && entry.date === date && entry.slotId === sourceSlotId);
    const replacement = cancelsAddedOccurrence
        ? clearedAt(base, date, sourceSlotId, now)
        : {
            kind: "cancelled",
            date,
            sourceSlotId,
            updatedAt: exceptionRevisionAt(base, `${date}:${sourceSlotId}`, now),
            updatedBy: base.memberId,
        };
    return normalizeMemberSchedule({
        ...base,
        exceptions: putExceptions(base, [replacement]),
        updatedAt: advance(now, base.updatedAt),
    });
}
/** Cancella le presenze da una data in avanti; lo storico ricorrente non è materializzato. */
export function cancelFutureBookings(schedule, fromDate, now = new Date().toISOString()) {
    const base = normalizeMemberSchedule(schedule);
    const recurring = putRecurring(base, base.recurring.map(({ slotId }) => ({ slotId, active: false })), now);
    const futureAdded = base.exceptions
        .filter((entry) => entry.kind === "added" && entry.date >= fromDate)
        .map((entry) => clearedAt(base, entry.date, exceptionSlotId(entry), now));
    return normalizeMemberSchedule({
        ...base,
        recurring,
        exceptions: putExceptions(base, futureAdded),
        updatedAt: advance(now, base.updatedAt),
    });
}
/**
 * Chi tiene occupata la giornata, fra gli impegni di quella data. Sta qui e non
 * nella schermata perche' la griglia — che ha gia' in mano la settimana — e la
 * scrittura devono rispondere alla stessa domanda nello stesso modo.
 */
export function dayHold(dayOccurrences, now = new Date().toISOString()) {
    const at = Date.parse(now);
    const [from, ...others] = dayOccurrences.filter((entry) => Date.parse(entry.startsAt) >= at);
    if (from)
        return { from, spent: false, others };
    const last = dayOccurrences.at(-1);
    return { from: last, spent: last !== undefined, others: [] };
}
export function planDayBooking(gym, schedule, date, targetSlotId, now = new Date().toISOString()) {
    const existing = occurrencesForDate(gym, schedule, date);
    const held = existing.find((entry) => entry.slotId === targetSlotId);
    if (held)
        return { kind: "kept", date, targetSlotId, occurrence: held };
    const hold = dayHold(existing, now);
    if (!hold.from)
        return { kind: "book", date, targetSlotId };
    if (hold.spent)
        return { kind: "spent", date, targetSlotId, from: hold.from };
    return {
        kind: "move",
        date,
        targetSlotId,
        from: hold.from,
        fromIsRecurring: schedule.recurringSlotIds.includes(hold.from.sourceSlotId),
        alsoCancelled: hold.others,
    };
}
/**
 * «Voglio esserci quel giorno a quell'ora», per una data sola. La strada — nuova
 * presenza o spostamento — la decide il vincolo, non chi chiama: cosi' vale per
 * qualunque schermata porti alla scrittura. Quando non c'e' niente da cambiare —
 * l'orario e' gia' suo, o la giornata e' stata usata da un impegno trascorso —
 * la pianificazione torna com'era: una seconda presenza nella stessa giornata non
 * puo' nascere da qui.
 */
export function applyDayBooking(gym, schedule, date, targetSlotId, now = new Date().toISOString()) {
    const plan = planDayBooking(gym, schedule, date, targetSlotId, now);
    if (plan.kind === "kept" || plan.kind === "spent")
        return normalizeMemberSchedule(schedule);
    if (plan.kind === "book")
        return bookOccurrence(schedule, date, targetSlotId, now);
    // Tornare sull'orario ricorrente che era stato spostato altrove non e' un
    // nuovo spostamento: e' il ritiro di quello di prima, cioe' la ricorrenza.
    const moved = plan.from.sourceSlotId === targetSlotId
        ? bookOccurrence(schedule, date, targetSlotId, now)
        : moveOccurrence(schedule, date, plan.from.sourceSlotId, targetSlotId, now);
    return plan.alsoCancelled.reduce((current, entry) => cancelOccurrence(current, date, entry.sourceSlotId, now), moved);
}
/**
 * Gli altri orari fissi che cadono nello stesso giorno della settimana del nuovo:
 * senza toglierli, un fisso aggiunto «d'ora in poi» si sommerebbe a quello che
 * c'era e la giornata tornerebbe doppia dalla settimana successiva.
 */
function recurringRivals(gym, schedule, targetSlotId, date) {
    const slotById = new Map(gym.slots.map((slot) => [slot.id, slot]));
    const weekday = slotById.get(targetSlotId)?.weekday ?? Temporal.PlainDate.from(date).dayOfWeek;
    return schedule.recurringSlotIds.filter((slotId) => slotId !== targetSlotId && slotById.get(slotId)?.weekday === weekday);
}
/**
 * «D'ora in poi»: cambia la ricorrenza e cancella le eccezioni che la
 * riguardavano. Le eccezioni non vengono rimosse ma sepolte, perche' un
 * dispositivo che non ha visto questa decisione le rimanderebbe indietro.
 */
export function applyRecurringChange(gym, schedule, date, occurrence, intent, now = new Date().toISOString()) {
    const base = normalizeMemberSchedule(schedule);
    const sourceSlotId = occurrence?.sourceSlotId;
    const targetSlotId = intent.kind === "cancel" ? undefined : intent.targetSlotId;
    // Una sola decisione per orario: due voci con la stessa revisione renderebbero
    // arbitrario chi vince fra "tolto" e "messo".
    const recurringChanges = new Map();
    const buried = new Map();
    const bury = (buriedDate, slotId) => buried.set(`${buriedDate}:${slotId}`, { date: buriedDate, slotId });
    const release = (slotId) => {
        recurringChanges.set(slotId, false);
        for (const entry of base.exceptions) {
            if (entry.kind === "cleared")
                continue;
            if (exceptionSlotId(entry) === slotId)
                bury(entry.date, exceptionSlotId(entry));
        }
    };
    if ((intent.kind === "move" || intent.kind === "cancel") && sourceSlotId)
        release(sourceSlotId);
    if (targetSlotId) {
        // I rilasci vengono prima del nuovo fisso: se coincidessero, resta.
        for (const rival of recurringRivals(gym, base, targetSlotId, date))
            release(rival);
        recurringChanges.set(targetSlotId, true);
        bury(date, targetSlotId);
    }
    const replacements = [...buried.values()].map((entry) => clearedAt(base, entry.date, entry.slotId, now));
    return normalizeMemberSchedule({
        ...base,
        recurring: putRecurring(base, [...recurringChanges].map(([slotId, active]) => ({ slotId, active })), now),
        exceptions: putExceptions(base, replacements),
        updatedAt: advance(now, base.updatedAt),
    });
}
export function recordConcurrentException(schedule, candidates) {
    const first = candidates[0];
    if (!first || candidates.length < 2)
        return schedule;
    const occurrenceKey = `${first.date}:${exceptionSlotId(first)}`;
    return {
        ...schedule,
        exceptions: schedule.exceptions.filter((entry) => `${entry.date}:${exceptionSlotId(entry)}` !== occurrenceKey),
        conflicts: [
            ...schedule.conflicts.filter((entry) => entry.occurrenceKey !== occurrenceKey),
            { occurrenceKey, candidates },
        ],
    };
}
export function resolveScheduleConflict(schedule, occurrenceKey, resolution) {
    return {
        ...schedule,
        exceptions: [
            ...schedule.exceptions.filter((entry) => `${entry.date}:${exceptionSlotId(entry)}` !== occurrenceKey),
            resolution,
        ],
        conflicts: schedule.conflicts.map((entry) => entry.occurrenceKey === occurrenceKey ? { ...entry, resolvedTo: resolution } : entry),
    };
}
